// Discover — สารบัญรายงานที่อยู่นอกเมนูปกติ
//
// เป็นกล่องเรียงกัน ไม่ใช่รายการเมนูย่อย เพราะรายงานแต่ละตัวมาจากคนละระบบและ
// ต้องอธิบายว่ามันตอบคำถามอะไรก่อนคนจะรู้ว่าควรกดอันไหน
//
// รายงานที่ผูกกับ Product Group จะแสดงเฉพาะกลุ่มที่เกี่ยวกับแบรนด์ที่เปิดอยู่:
// อยู่ที่กลุ่มก็คือกลุ่มนั้น อยู่ที่สินค้าก็คือกลุ่มที่สินค้านั้นสังกัด — ไม่ใช่
// กลุ่มทั้งหมดที่มีสิทธิ์ ซึ่งสำหรับ Admin คือทุกกลุ่มในระบบและอ่านไม่ไหว
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import type { Brand } from "@contracts/api";
import { menuPath } from "@features/navigation/model/side-menus";
import { fetchGroups, settingsKeys } from "@features/settings/api/settings-api";
import { MENU_ICONS } from "@shared/ui/menu-icons";
import { REPORTS, type DiscoverReport } from "../model/reports";

type Props = { brand: Brand; brands: Brand[] };

export function DiscoverPage({ brand, brands }: Props) {
  const isGroup = brand.kind === "group";
  // เปิดจากสินค้า ต้องรู้ว่าสินค้านี้อยู่กลุ่มไหน ซึ่งมีแต่ในแคตตาล็อกกลุ่ม
  // (รายการแบรนด์ไม่ได้พก members มาด้วย) — อยู่ที่กลุ่มอยู่แล้วก็ไม่ต้องถาม
  const { data: catalog, isPending } = useQuery({
    queryKey: settingsKeys.groups,
    queryFn: fetchGroups,
    enabled: !isGroup,
    staleTime: 5 * 60_000,
  });

  const groups = isGroup
    ? [brand]
    : (catalog ?? [])
        .filter((g) => g.members.includes(brand.id))
        // ต้องมีสิทธิ์กลุ่มนั้นด้วย ไม่ใช่แค่สินค้าอยู่ในกลุ่ม
        .map((g) => brands.find((b) => b.id === g.id))
        .filter((b): b is Brand => !!b);

  const groupReports = REPORTS.filter((r) => r.groupOnly);
  const plainReports = REPORTS.filter((r) => !r.groupOnly);
  const waiting = !isGroup && isPending;

  return (
    <>
      <div className="breadcrumb">{brand.name} / Discover</div>
      <h1>Discover</h1>
      <p className="muted small-note">
        รายงานเชิงลึกจากระบบในเครือ เลือกกล่องที่ต้องการเพื่อเปิดดู
      </p>

      <div className="discover-grid">
        {plainReports.map((r) => (
          <PlainCard key={r.slug} report={r} brand={brand} />
        ))}
        {groupReports.flatMap((r) =>
          groups.map((g) => <GroupCard key={`${r.slug}:${g.id}`} report={r} group={g} />),
        )}
        {/* สินค้าที่ไม่ได้อยู่กลุ่มไหนเลย = กล่องกลุ่มหายไปเฉย ๆ ต้องบอกว่าทำไม */}
        {!waiting && !groups.length && groupReports.length > 0 && (
          <div className="discover-card is-off">
            <span className="discover-title">{groupReports[0].title}</span>
            <span className="discover-desc">{groupReports[0].desc}</span>
            <span className="discover-off-note">
              {brand.name} ยังไม่ได้อยู่ใน Product Group ไหน — รายงานนี้เทียบทั้งกลุ่ม
              จึงยังไม่มีข้อมูลให้ดู จัดกลุ่มได้ที่ Setting → Group
            </span>
          </div>
        )}
      </div>
    </>
  );
}

function CardBody({ report }: { report: DiscoverReport }) {
  return (
    <>
      <span className="discover-title">{report.title}</span>
      <span className="discover-desc">{report.desc}</span>
      <span className="discover-tag">{report.tag}</span>
    </>
  );
}

/**
 * รายงานของ Product Group — ไม่ต้องขึ้นชื่อกลุ่มบนกล่องแล้ว เพราะหน้านี้แสดงเฉพาะ
 * กลุ่มที่เปิดอยู่ ชื่อกลุ่มจึงซ้ำกับที่อยู่บน breadcrumb และแถบซ้ายอยู่แล้ว
 * ที่คนอยากรู้จริง ๆ คือรายงานนี้เป็นรอบไหน
 */
function GroupCard({ report, group }: { report: DiscoverReport; group: Brand }) {
  const Icon = MENU_ICONS[report.icon];
  return (
    <Link className="discover-card" to={menuPath(group.id, "discover", report.slug)}>
      <span className="discover-icon">
        <Icon />
      </span>
      <span className="discover-kicker">Monthly / Quarterly Report</span>
      <CardBody report={report} />
    </Link>
  );
}

function PlainCard({ report, brand }: { report: DiscoverReport; brand: Brand }) {
  const Icon = MENU_ICONS[report.icon];
  return (
    <Link className="discover-card" to={menuPath(brand.id, "discover", report.slug)}>
      <span className="discover-icon">
        <Icon />
      </span>
      <CardBody report={report} />
    </Link>
  );
}
